/**
 * Back in Stock — Reston Farm Garden Market
 *
 * Swaps the buy buttons for a "notify me" form when the selected variant is
 * sold out, and posts signups to Klaviyo's client Back in Stock API:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=<public key>
 * Klaviyo then fires its Back in Stock flow when the variant's Shopify
 * quantity goes from 0 to 1+.
 *
 * Built to survive Horizon re-rendering the product block on variant change:
 * all listeners are delegated from document, and state is re-read from the
 * DOM every time.
 */
(function () {
  'use strict';

  if (window.RFBackInStock) return;

  var API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-10-15';
  var STORE_KEY = 'rf_bis_subs_v1';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* ---------- small helpers ---------- */

  function readSubs() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }

  function saveSub(variantId, email) {
    try {
      var s = readSubs();
      s[variantId] = { e: email, t: Date.now() };
      window.localStorage.setItem(STORE_KEY, JSON.stringify(s));
    } catch (e) { /* storage blocked: the form still works, it just won't remember */ }
  }

  function forgetSub(variantId) {
    try {
      var s = readSubs();
      delete s[variantId];
      window.localStorage.setItem(STORE_KEY, JSON.stringify(s));
    } catch (e) {}
  }

  function variantsOf(root) {
    var el = root.querySelector('[data-rf-bis-variants]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function sectionOf(root) {
    return root.closest('.shopify-section') || document.body;
  }

  function currentVariantId(root) {
    var section = sectionOf(root);
    var input = section.querySelector('product-form-component form input[name="id"]') ||
                section.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value);
    var hidden = root.querySelector('[data-rf-bis-variant]');
    return hidden ? String(hidden.value) : '';
  }

  function fill(tpl, email, product) {
    return String(tpl || '').replace(/\[email\]/g, email).replace(/\[product\]/g, product);
  }

  /* ---------- render ---------- */

  function apply(root, variantId, availableHint) {
    var vars = variantsOf(root);
    var id = String(variantId || currentVariantId(root) || '');
    var available;
    if (typeof availableHint === 'boolean') available = availableHint;
    else if (vars[id]) available = Boolean(vars[id].a);
    else available = true;

    var designPreview = root.dataset.designMode === 'true';
    root.hidden = available && !designPreview;

    var section = sectionOf(root);
    if (!designPreview) section.classList.toggle('rf-bis-active', !available);

    var hidden = root.querySelector('[data-rf-bis-variant]');
    if (hidden && id) hidden.value = id;

    var label = root.querySelector('[data-rf-bis-variant-label]');
    if (label) {
      var vt = vars[id] && vars[id].t;
      if (vt && vt !== 'Default Title') { label.textContent = ' · ' + vt; label.hidden = false; }
      else { label.textContent = ''; label.hidden = true; }
    }

    var subs = readSubs();
    showState(root, subs[id] ? subs[id].e : null);
  }

  function showState(root, email) {
    var form = root.querySelector('[data-rf-bis-form]');
    var done = root.querySelector('[data-rf-bis-done]');
    if (!form || !done) return;
    if (email) {
      var text = done.querySelector('[data-rf-bis-done-text]');
      if (text) text.textContent = fill(root.dataset.success, email, productLabel(root));
      form.hidden = true;
      done.hidden = false;
    } else {
      form.hidden = false;
      done.hidden = true;
    }
  }

  function productLabel(root) {
    var vars = variantsOf(root);
    var id = (root.querySelector('[data-rf-bis-variant]') || {}).value;
    var t = root.dataset.productTitle || 'this item';
    var vt = vars[id] && vars[id].t;
    if (vt && vt !== 'Default Title') t += ' (' + vt + ')';
    return t;
  }

  function setMsg(root, text) {
    var msg = root.querySelector('[data-rf-bis-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
  }

  function setBusy(root, busy) {
    var btn = root.querySelector('[data-rf-bis-btn]');
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('rf-bis__btn--busy', busy);
    btn.setAttribute('aria-busy', String(busy));
  }

  /* ---------- submit ---------- */

  function subscribe(root, email, variantId) {
    var company = root.dataset.company;
    var body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
        }
      }
    };

    return fetch(API + '?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: {
        'accept': 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        'revision': REVISION
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return { ok: true };
      return res.text().then(function (t) {
        /* Klaviyo rejects a second signup for the same person + variant.
           From the shopper's side that is a success: they're already on the list. */
        var dup = /duplicate|already|exists/i.test(t || '');
        return { ok: dup, status: res.status, detail: t };
      });
    });
  }

  function onSubmit(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-rf-bis-form]')) return;
    e.preventDefault();

    var root = form.closest('[data-rf-bis]');
    if (!root) return;

    var input = form.querySelector('input[name="email"]');
    var email = (input && input.value || '').trim().toLowerCase();
    var trap = form.querySelector('input[name="rf_bis_company"]');
    var variantId = (root.querySelector('[data-rf-bis-variant]') || {}).value || currentVariantId(root);

    setMsg(root, '');

    if (!EMAIL_RE.test(email)) {
      setMsg(root, root.dataset.invalid);
      if (input) input.focus();
      return;
    }

    /* Bots fill every field. Pretend it worked and send nothing. */
    if (trap && trap.value) {
      saveSub(variantId, email);
      showState(root, email);
      return;
    }

    setBusy(root, true);
    subscribe(root, email, variantId)
      .then(function (r) {
        if (!r.ok) throw r;
        saveSub(variantId, email);
        showState(root, email);
        try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email: email }); } catch (err) {}
        document.dispatchEvent(new CustomEvent('rf:bis:subscribed', { detail: { variantId: variantId } }));
      })
      .catch(function (err) {
        console.error('[RFBackInStock] signup failed', err);
        setMsg(root, root.dataset.error);
      })
      .then(function () { setBusy(root, false); });
  }

  function onClick(e) {
    var reset = e.target && e.target.closest && e.target.closest('[data-rf-bis-reset]');
    if (!reset) return;
    var root = reset.closest('[data-rf-bis]');
    if (!root) return;
    var id = (root.querySelector('[data-rf-bis-variant]') || {}).value;
    forgetSub(id);
    showState(root, null);
    var input = root.querySelector('input[name="email"]');
    if (input) { input.value = ''; input.focus(); }
  }

  /* ---------- variant changes ---------- */

  function onVariant(e) {
    var scope = (e.target && e.target.closest && e.target.closest('.shopify-section')) || document;
    var detail = e.detail || {};
    var res = detail.resource || null;
    var id = res && res.id ? String(res.id) : null;
    var avail = res && typeof res.available === 'boolean' ? res.available : undefined;

    function run() {
      scope.querySelectorAll('[data-rf-bis]').forEach(function (root) { apply(root, id, avail); });
    }
    run();
    /* Horizon may morph the block a beat later; re-apply after it settles.
       setTimeout, not requestAnimationFrame, so it also runs in background tabs. */
    setTimeout(run, 80);
    setTimeout(run, 400);
  }

  function initAll() {
    document.querySelectorAll('[data-rf-bis]').forEach(function (root) { apply(root); });
  }

  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('click', onClick);
  document.addEventListener('variant:update', onVariant, true);
  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  window.RFBackInStock = { refresh: initAll };
})();
